import React, { useEffect, useMemo, useState } from 'react'
import { logActions } from 'utils/constants'
import styled from 'styled-components'
import { calendarDate, formatDate, formatDateWithTime } from 'utils/date'
import { pathOr, prop, propOr, sortBy, reverse, keys, isEmpty, values } from 'ramda'
import { ReactComponent as FileIcon } from 'assets/images/file-icon.svg'
import { toCamelCase, toTextCase } from 'js-convert-case/lib'
import { roleLabel } from 'utils/roleOptions'
import { handleBooleanValue } from 'utils/hooks/values'
import { useSelector, useDispatch } from 'react-redux'
import { getBuildingTypes } from 'features/bpList/ducks/selectors'
import { getBuildingTypeValueType, getFloorName } from 'utils/bpData'
import { isNotNilOrEmpty } from 'utils/ramda'
import ActivityExpandablePanel from 'features/bpList/Components/BpInfo/ActivityExpandablePanel'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { getBpActivityLog, getExternalFileAccessLogs } from 'services/BpService'
import { CircularProgress } from '@mui/material'
import { restoreFileRoutine } from 'ducks/files/actions'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

const getActivityActor = logElement => {
  const { user, action } = logElement
  const userFullName = `${propOr('', 'firstName', user)} ${propOr('', 'lastName', user)}`.trim()
  if (user && userFullName) return userFullName

  if (action === 'building_passport.files.update') {
    const changes = logElement.data?.changes?.attributes || {}
    if (changes.last_indexed_at) return 'Document Processing System'
    if (changes.file_type_id || changes.description) return 'AI Classification System'
    return 'File Management System'
  }
  if (action === 'user.invitation_reminder.sent') return 'Notification System'
  if (action === 'building_passport.update') return 'Building Passport System'
  return 'System'
}

const getActivityMessage = logElement => {
  const log = logActions.find(e => e.key === logElement.action)
  const actor = getActivityActor(logElement)
  if (log?.key?.includes('file_unshared')) return `${actor} removed access to a file`
  if (log?.key === 'building_passport.file_shared_internal' || log?.key === 'building_passport.external_file_shared') return `${actor} shared a file`
  return `${actor} ${log?.value || ''}`.trim()
}

const groupConsecutiveActivity = items => {
  const groups = []
  items.forEach(item => {
    const groupKey = `${item.action}::${getActivityActor(item)}`
    const last = groups[groups.length - 1]
    if (last && last.groupKey === groupKey) last.items.push(item)
    else groups.push({ groupKey, items: [item] })
  })
  return groups
}

const Activity = () => {
  const bp = useSelector(getSelectedBp)
  const dispatch = useDispatch()
  const [auditLog, setAuditLog] = useState([])
  const [loading, setLoading] = useState(false)
  const [restoringFileIds, setRestoringFileIds] = useState({})
  const buildingTypes = useSelector(getBuildingTypes)

  const [refreshKey, setRefreshKey] = useState(0)

  const handleRestoreFile = (fileId) => {
    setRestoringFileIds(prev => ({ ...prev, [fileId]: true }))
    dispatch(restoreFileRoutine({
      bpId: bp.id,
      fileId,
      callback: () => {
        setRestoringFileIds(prev => ({ ...prev, [fileId]: false }))
        setRefreshKey(prev => prev + 1)
      }
    }))
  }

  const filteredAuditLog = useMemo(() => {
    const filtered = auditLog.filter(logItem =>
      !['file_external_download', 'file_external_preview', 'action.dismissed'].includes(logItem.action)
    )

    // Remove redundant building_passport.update entries that occur immediately after file additions
    return filtered.filter((logItem, index) => {
      if (logItem.action === 'building_passport.update') {
        // Check if this update has no meaningful changes (only updated_at changed)
        const hasNoMeaningfulChanges = !logItem.data?.changes?.attributes || 
          Object.keys(logItem.data.changes.attributes).length === 0

        if (hasNoMeaningfulChanges) {
          // Check if there's a files.add entry within 10 seconds before this update
          const logTime = new Date(logItem.createdAt)
          const hasRecentFileAdd = filtered.some((otherItem) => {
            if (otherItem.action === 'building_passport.files.add') {
              const otherTime = new Date(otherItem.createdAt)
              const timeDiff = Math.abs(logTime - otherTime)
              return timeDiff <= 10000 // 10 seconds
            }
            return false
          })

          // Filter out this redundant update if there's a recent file addition
          if (hasRecentFileAdd) {
            return false
          }
        }
      }
      return true
    })
  }, [auditLog])

  const maskEmail = email => {
    if (!email || !email.includes('@')) return email

    const [localPart, domain] = email.split('@')
    const maskedLocal = localPart[0] + '*'.repeat(localPart.length - 1)
    const domainSuffix = domain.slice(domain.indexOf('.'))

    return `${maskedLocal}@*****${domainSuffix}`
  }

  useEffect(() => {
    setLoading(true)

    Promise.all([
      getBpActivityLog({ id: bp.id }),
      getExternalFileAccessLogs(bp.id)
    ])
      .then(([auditResp, externalResp]) => {
        const internalLogs = auditResp.data.data || []

        const externalLogsRaw = externalResp.data.data || []

        const externalLogs = externalLogsRaw.map(log => {
          let email = log.email

          if (!email && log.file?.shares?.length && log.shareToken) {
            const matchedShare = log.file.shares.find(s => s.share_token === log.shareToken)
            email = matchedShare?.email || null
          }

          const maskedEmail = email ? maskEmail(email) : 'External'

          return {
            ...log,
            email: maskedEmail,
            action: `file_external_${log.action}`,
            user: {
              firstName: maskedEmail,
              lastName: ''
            },
            createdAt: log.createdAt || log.created_at,
            data: {
              fileName: log.file?.display_name || 'Unknown file',
              shareToken: log.share_token,
              sharedWith: maskedEmail,
              sharedWithFullName: log.sharedWithFullName || null
            }
          }
        })

        const internalLogsProcessed = internalLogs.map(log => {
          const action = log?.action || ''
          const data = log?.data || {}
          const fullName = data.sharedWithFullName
          const sharedWith = data.sharedWith

          const shouldMask = (action.includes('file_shared_internal') || action.includes('file_shared_external'))

          return {
            ...log,
            data: {
              ...data,
              sharedWith: shouldMask && sharedWith?.includes('@') ? maskEmail(sharedWith) : sharedWith,
              sharedWithFullName: fullName
            }
          }
        })

        setAuditLog([...internalLogsProcessed])

      })
      .catch(() => setAuditLog([]))
      .finally(() => setLoading(false))
  }, [bp, refreshKey])

  const renderChangedValue = (changes, key) => {
    const valueType = typeof changes[key]

    switch (true) {
      case valueType === 'boolean':
        return handleBooleanValue(changes[key])
      case key === 'buildingTypes':
        const typesObject = changes[key]
        return typesObject.map(el => {
          const type = propOr(
            { label: '', unit: '' },
            toCamelCase(el.type),
            buildingTypes)
          return (
            <BuildingTypes key={el.type}>
              <div>{type.label}</div>
              <BuildingTypeWrapper>
                <span>{getBuildingTypeValueType(el.type).replace(' (sqm)', '')}</span>
                <div>{type.unit === 'unit' ? el.units : el.area}&nbsp;{type.unit}</div>
              </BuildingTypeWrapper>
            </BuildingTypes>
          )
        })
      default:
        return changes[key]
    }
  }

  const getBpChanges = logElement => {
    const changes = pathOr({}, ['data', 'changes', 'attributes'], logElement)
    const objectKeys = keys(changes)
    return objectKeys && objectKeys.map(keyName => (
      <ListItem key={`bp-change-${keyName}`}>
        {toTextCase(keyName === 'hoardings' ? 'hoarding' : keyName)}:
        <div>{renderChangedValue(changes, keyName)}</div>
      </ListItem>
    ))
  }

  const getContent = (log, logElement) => {
    const data = logElement.data || {}

    switch (true) {
      case log && log.key.includes('user'):
        return (
          <UpdatedBpWrapper>
            <ListItem>
              User name:
              <div>{pathOr('', ['data', 'user', 'firstName'], logElement)} {pathOr('', ['data', 'user', 'lastName'], logElement)}</div>
            </ListItem>
            {typeof data.relationType === 'string' && data.relationType && (
              <ListItem>
                Role:
                <div>{roleLabel(data.relationType)}</div>
              </ListItem>
            )}
            {data.via?.type && (
              <ListItem>
                Access through:
                <div>{data.via.type === 'estate' ? 'Estate' : 'Portfolio'}{data.via.name ? ` ${data.via.name}` : ''}</div>
              </ListItem>
            )}
          </UpdatedBpWrapper>
        )
      case log && log.key.includes('update'):
        return (
          <UpdatedBpWrapper>
            {getBpChanges(logElement)}
          </UpdatedBpWrapper>
        )
      case log && log.key.includes('notes'):
        const floorNumber = pathOr('', ['data', 'floorNumber'], logElement)

        return (
          <NoteContentWrapper>
            {!isNotNilOrEmpty(floorNumber) ? 'General note' : getFloorName(String(floorNumber))}
            <div>{`"${pathOr('', ['data', 'note'], logElement)}"`}</div>
          </NoteContentWrapper>
        )
      case log && log.key.includes('files'): {
        const fileData = pathOr(null, ['data', 'files', 0], logElement)
        const isDeleteAction = log.key === 'building_passport.files.delete'
        const fileIsStillDeleted = fileData?.deleted === true
        const fileId = fileData?.id
        const isRestoring = fileId && restoringFileIds[fileId]

        return (
          <FileContentWrapper>
            <FileIcon />
            <p>
              <a href={pathOr('', ['url'], fileData)} target='_blank' rel='noreferrer'>
                {pathOr('', ['name'], fileData)}
              </a>
              {log.key.endsWith('.new_version') && data.version ? ` (version ${data.version})` : ''}
            </p>
            {isDeleteAction && fileIsStillDeleted && fileId && (
              <RestoreButton
                disabled={isRestoring}
                onClick={() => handleRestoreFile(fileId)}
              >
                {isRestoring ? 'Restoring...' : 'Restore'}
              </RestoreButton>
            )}
          </FileContentWrapper>
        )
      }
      // case log && log.key.includes('file_external'):
      //   return (
      //     <FileContentWrapper>
      //       <FileIcon />
      //       <div>
      //         <div><strong>File:</strong> {logElement?.file?.displayName || 'Unknown file'}</div>
      //         <div><strong>IP:</strong> {logElement.ipAddress}</div>
      //
      //       </div>
      //     </FileContentWrapper>
      //   )
      case log && log.key.includes('units.add'):
        return (
          <UnitsContentWrapper>
            <BuildingTypeWrapper>
              <span>Type:</span>
              {pathOr(
                '',
                [toCamelCase(pathOr('', ['data', 'units', 0, 'buildingType'], logElement)), 'label'],
                buildingTypes
              )}
            </BuildingTypeWrapper>
            <BuildingTypeWrapper>
              <span>Number of units:</span>
              {pathOr([], ['data', 'units'], logElement).length}
            </BuildingTypeWrapper>
          </UnitsContentWrapper>
        )
      case log && log.key.includes('units.delete'):
        return (
          <UnitsContentWrapper>
            <BuildingTypeWrapper>
              {values(pathOr({}, ['data'], logElement))[0]}
            </BuildingTypeWrapper>
          </UnitsContentWrapper>
        )
      case log && log.key.includes('file_unshared'):
        return (
          <FileContentWrapper>
            <FileIcon />
            <div>
              <div><strong>File:</strong> {data.fileName || 'Unknown file'}</div>
              <div><strong>Unshared from:</strong> {data.unsharedWithFullName || data.unsharedWith || 'Unknown user'}</div>
            </div>
          </FileContentWrapper>
        )
      case log?.key.includes('file_shared_internal') || log?.key.includes('file_shared_external'):
        return (
          <FileContentWrapper>
            <FileIcon />
            <div>
              <div><strong>File:</strong> {data.fileName || 'Unknown file'}</div>
              <div>
                <div>
                  <div>
                    <strong>Shared with:</strong>{' '}
                    {data.sharedWith?.includes('@')
                      ? maskEmail(data.sharedWith)
                      : data.sharedWithFullName || 'Unknown user'}
                  </div>

                </div>

              </div>
            </div>
          </FileContentWrapper>
        )
      default:
        return <div/>
    }
  }

  const renderActivity = logElement => {
    const {user, action, createdAt } = logElement
    const log = logActions.find(e => e.key === action)

    const userFullName = `${propOr('', 'firstName', user)} ${propOr('', 'lastName', user)}`.trim()
    
    // Determine if this is a system action (no user_id)
    const isSystemAction = !user || !userFullName
    
    // Create appropriate actor based on action type
    const getActor = () => {
      if (!isSystemAction) {
        return userFullName
      }
      
      // System actions - provide more specific context
      if (action === 'building_passport.files.update') {
        // Check if it's file indexing or file type/description updates
        const data = logElement.data || {}
        if (data.changes?.attributes?.last_indexed_at) {
          return 'Document Processing System'
        } else if (data.changes?.attributes?.file_type_id || data.changes?.attributes?.description) {
          return 'AI Classification System'
        }
        return 'File Management System'
      } else if (action === 'user.invitation_reminder.sent') {
        return 'Notification System'
      } else if (action === 'building_passport.update') {
        return 'Building Passport System'
      } else {
        return 'System'
      }
    }

    const actor = getActor()
    const isUnshared = log?.key?.includes('file_unshared')
    const isSharedInternal = log?.key === 'building_passport.file_shared_internal'
    const isSharedExternal = log?.key === 'building_passport.external_file_shared'

    const message = isUnshared
      ? `${actor} removed access to a file`
      : isSharedInternal || isSharedExternal
        ? `${actor} shared a file`
        : `${actor} ${log?.value || ''}`

    return (
      <ActivityWrapper>
        <ActivityHeaderWrapper>
          <Bullet>&bull;</Bullet>
          {message}
        </ActivityHeaderWrapper>
        <CreationDate>
          {formatDateWithTime(createdAt)}
        </CreationDate>
        <ActivityContent>
          {getContent(log, logElement)}
        </ActivityContent>
      </ActivityWrapper>
    )
  }
  const [panelStatuses, setPanelStatuses] = useState({})
  const [openGroups, setOpenGroups] = useState({})
  const toggleGroup = gid => setOpenGroups(prev => ({ ...prev, [gid]: !prev[gid] }))
  const toggleSinglePanel = key => () => {
    const newState = { ...panelStatuses }
    newState[key] = !newState[key]
    setPanelStatuses(newState)
    }
  const expandAll = () => {
    const newState = {}
    for (const key in panelStatuses) {
      newState[key] = true
    }
    setPanelStatuses(newState)
  }
  const collapseAll = () => {
    const newState = {}
    for (const key in panelStatuses) {
      newState[key] = false
    }
    setPanelStatuses(newState)
  }

  useEffect(() => {
    let panelStates = {}
    filteredAuditLog.forEach(log => {
      const date = calendarDate(log.createdAt)
      panelStates[date] = false
    })
    setPanelStatuses(panelStates)
  }, [filteredAuditLog])

  const logsByDates = useMemo(() => {
    let obj = {}
    filteredAuditLog.forEach(log => {
      const date = calendarDate(log.createdAt)
      obj[date] = [...pathOr([], [date], obj), log]
    })
    const objKeys = keys(obj) ? keys(obj).sort((a, b) => new Date(b) - new Date(a)) : []
    return objKeys.map(key => {
      const title = formatDate(key)
      const sorted = obj[key] ? reverse(sortBy(prop('createdAt'))(obj[key])) : []
      return (
        <ActivityExpandablePanel
          key={key}
          title={title}
          expanded={Boolean(panelStatuses[key]).toString()}
          onToggle={toggleSinglePanel(key)}
        >
          {groupConsecutiveActivity(sorted).map((group, gi) => {
            if (group.items.length === 1) {
              const only = group.items[0]
              return <React.Fragment key={only.id || `${key}-${gi}`}>{renderActivity(only)}</React.Fragment>
            }
            const gid = `${key}-${gi}-${group.groupKey}`
            const isOpen = Boolean(openGroups[gid])
            return (
              <GroupedActivity key={gid}>
                <GroupedHeader onClick={() => toggleGroup(gid)}>
                  <GroupedSummary>
                    <Bullet>&bull;</Bullet>
                    {getActivityMessage(group.items[0])}
                    <GroupTimes>{group.items.length} times</GroupTimes>
                  </GroupedSummary>
                  {isOpen ? <KeyboardArrowUpIcon fontSize='small' /> : <KeyboardArrowDownIcon fontSize='small' />}
                </GroupedHeader>
                <CreationDate>{formatDateWithTime(group.items[0].createdAt)}</CreationDate>
                {isOpen && (
                  <GroupedItems>
                    {group.items.map((it, ii) => (
                      <React.Fragment key={it.id || `${gid}-${ii}`}>{renderActivity(it)}</React.Fragment>
                    ))}
                  </GroupedItems>
                )}
              </GroupedActivity>
            )
          })}
        </ActivityExpandablePanel>
      )
    })
  }, [filteredAuditLog, panelStatuses, openGroups])

  return (
    <div>
      {loading ? (
        <LoadingWrapper>
          <CircularProgress />
        </LoadingWrapper>
      ) : (
        <>
          <ButtonWrapper>
            {logsByDates.length > 1 && <ExpandCollapseButton onClick={expandAll}>Expand all</ExpandCollapseButton>}
            {logsByDates.length > 1 && <ExpandCollapseButton onClick={collapseAll}>Collapse all</ExpandCollapseButton>}
          </ButtonWrapper>
          {logsByDates}
        </>
      )}
    </div>
  )
}

export default Activity

const ExpandCollapseButton = styled.button`
  border-radius: 4px;
  font-size: 12px;
  box-shadow: none;
  background-color: #dfe4f6;
  border: none;
  margin-bottom: 7px;
  padding: 5px 10px;

  :hover {
    background-color: ${({ theme }) => theme.colors.secondary};
    color: #fff;
  }
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-bottom: 5px;
`
const GroupedActivity = styled.div`
  box-sizing: border-box;
  margin-bottom: 8px;
`
const GroupedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
`
const GroupedSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`
const GroupTimes = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #667085;
  background: #eef0f3;
  border-radius: 999px;
  padding: 1px 8px;
`
const GroupedItems = styled.div`
  margin: 6px 0 0 4px;
  padding: 6px 0 2px 14px;
  border-left: 2px solid #e4e7ec;
`

const ActivityWrapper = styled.div`
  box-sizing: border-box;
  margin-bottom: 25px;
  max-width: 100%;
`

const ActivityHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  ${({ theme }) => theme.colors.haiti};
`

const ActivityContent = styled.div``

const Bullet = styled.span`
  display: inline-block;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 25px;
  margin-right: 10px;
`

const CreationDate = styled.div`
  padding-left: 35px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.haiti};
  opacity: .4;
`

const NoteContentWrapper = styled.div`
  box-sizing: border-box;
  padding: 10px 0 0 35px;
  width: 100%;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.darkGrey};

  & > div {
    opacity: .7;
  }
`

const FileContentWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 10px 0 0 35px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.darkGrey};
  width: 95%;

  p {
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    min-width: 19px;
    min-height: 17px;
    margin-right: 10px;
  }
`

const UnitsContentWrapper = styled.div`
  box-sizing: border-box;
  padding: 10px 0 0 35px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.darkGrey};
  width: 95%;

  p {
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    min-width: 19px;
    min-height: 17px;
    margin-right: 10px;
  }
`

const ListItem = styled.div`
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 12px;
  margin-bottom: 7px;

  &:first-letter {
    text-transform: uppercase;
  }

  & > div {
    opacity: .7;
  }
`

const UpdatedBpWrapper = styled.div`
  padding: 10px 0 0 35px;
`

const BuildingTypeWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 7px;

  div, span {
    opacity: 1;
  }
`

const BuildingTypes = styled.div`
  opacity: 1;
`

const LoadingWrapper = styled.div`
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const RestoreButton = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  padding: 3px 10px;
  font-size: 11px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background-color: #f9fafb;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: #e5e7eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
